/**
 * Тонкая обёртка над Telegram WebApp SDK.
 *
 * Все вызовы через опциональную цепочку: в обычном браузере `window.Telegram`
 * отсутствует, и вёрстка обязана открываться без падения.
 *
 * Правила и грабли — .claude/skills/telegram-miniapp.
 */

interface SafeAreaInset {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

interface TelegramWebApp {
  initData?: string;
  initDataUnsafe?: { user?: { id: number; language_code?: string }; start_param?: string };
  colorScheme?: 'light' | 'dark';
  safeAreaInset?: SafeAreaInset;
  ready?: () => void;
  expand?: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent?: (event: string, handler: () => void) => void;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (h: () => void) => void;
    offClick: (h: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: string) => void;
    notificationOccurred: (type: string) => void;
  };
  LocationManager?: {
    init: (cb: () => void) => void;
    isLocationAvailable?: boolean;
    /** Запрашивали ли доступ хоть раз — отличает «ещё не спросили» от «отказали». */
    isAccessRequested?: boolean;
    isAccessGranted?: boolean;
    getLocation: (cb: (loc: { latitude: number; longitude: number } | null) => void) => void;
    /** Открывает системные настройки доступа к геолокации для этого бота. */
    openSettings?: () => void;
  };
  /** Показывает нативный попап «поделиться номером» — Bot API 6.9+. */
  requestContact?: (callback?: (shared: boolean) => void) => void;
  /** Открывает t.me-ссылку внутри Telegram. С Bot API 7.0+ мини-апп не закрывается. */
  openTelegramLink?: (url: string) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | undefined =
  typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

export const isTelegram = Boolean(tg);

/**
 * Подписанные данные для авторизации. На сервер уходит именно эта строка —
 * `initDataUnsafe` не отправляется никогда, это неподписанные данные.
 */
export const initData = tg?.initData ?? '';

export function startParam(): string {
  return tg?.initDataUnsafe?.start_param ?? '';
}

export function telegramLanguage(): string {
  return tg?.initDataUnsafe?.user?.language_code ?? '';
}

/**
 * Инсеты безопасной зоны.
 * На Android CSS `env(safe-area-inset-*)` равен нулю, и системная навигация
 * перекрывает нижнюю панель. Значения берём из SDK; `max()` сохраняет
 * корректность на iOS, где `env()` работает.
 */
function applyInsets(): void {
  if (!tg) return;
  const root = document.documentElement;

  const apply = (): void => {
    const sa = tg.safeAreaInset ?? {};
    const top = Number(sa.top) || 0;
    const bottom = Number(sa.bottom) || 0;
    root.style.setProperty('--tg-safe-top', `max(env(safe-area-inset-top, 0px), ${top}px)`);
    root.style.setProperty('--tg-safe-bottom', `max(env(safe-area-inset-bottom, 0px), ${bottom}px)`);
  };

  apply();
  tg.onEvent?.('safeAreaChanged', apply);
  tg.onEvent?.('viewportChanged', apply);
}

/**
 * Тема Mini App фиксированно светлая. Витрина еды должна оставаться тёплой
 * независимо от системной темы телефона: иначе Telegram dark mode превращает
 * клиентский интерфейс в совсем другой продукт.
 */
function applyTheme(): void {
  const root = document.documentElement;
  const appBg = '#fff7f3';

  const set = (): void => {
    root.dataset.theme = 'light';
    tg?.setHeaderColor?.(appBg);
    tg?.setBackgroundColor?.(appBg);
  };

  set();
  tg?.onEvent?.('themeChanged', set);
}

export function initTelegram(): void {
  applyTheme();
  if (!tg) return;

  tg.ready?.();
  tg.expand?.();
  // Без этого вертикальный свайп по списку закрывает мини-апп
  tg.disableVerticalSwipes?.();
  applyInsets();
}

/**
 * Стек обработчиков «назад»: кнопка у Telegram одна, а владельцев бывает
 * несколько — экран и открытое поверх него окно (полноэкранная карта зоны).
 * Привязан всегда только верхний: если подписать обоих, одно нажатие уведёт
 * на два шага назад, и выглядеть это будет как «назад перескакивает экран».
 */
const backHandlers: (() => void)[] = [];
let boundHandler: (() => void) | null = null;

function syncBackButton(): void {
  const bb = tg?.BackButton;
  if (!bb) return;

  const top = backHandlers[backHandlers.length - 1] ?? null;
  if (top === boundHandler) return;

  try {
    if (boundHandler) bb.offClick(boundHandler);
    boundHandler = top;
    if (top) {
      bb.onClick(top);
      bb.show();
    } else {
      bb.hide();
    }
  } catch {
    // старый клиент — не критично
  }
}

/**
 * Системная кнопка «назад» Telegram.
 * Возвращает функцию снятия — вызывать обязательно при размонтировании:
 * забытый обработчик остаётся в стеке и перехватывает чужое «назад».
 * Самая частая ошибка в мини-аппах.
 */
export function showBackButton(handler: () => void): () => void {
  backHandlers.push(handler);
  syncBackButton();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const i = backHandlers.lastIndexOf(handler);
    if (i >= 0) backHandlers.splice(i, 1);
    syncBackButton();
  };
}

export function haptic(type: string = 'light'): void {
  const h = tg?.HapticFeedback;
  if (!h) return;
  try {
    if (['light', 'medium', 'heavy', 'rigid', 'soft'].includes(type)) {
      h.impactOccurred(type);
    } else if (['success', 'error', 'warning'].includes(type)) {
      h.notificationOccurred(type);
    }
  } catch {
    // старый клиент Telegram — молча пропускаем
  }
}

/**
 * Доступна ли геолокация Telegram на этом клиенте.
 * LocationManager появился в Bot API 8.0 — на старых версиях кнопку прячем,
 * чтобы не предлагать то, что заведомо не сработает.
 */
export function isLocationAvailable(): boolean {
  return Boolean(tg?.LocationManager);
}

/**
 * Почему не удалось получить геолокацию — экран решает, что показать.
 *
 * `denied` отличается от остальных: доступ спрашивали и отказали, значит
 * есть смысл предложить открыть системные настройки. `unsupported` и
 * `unavailable` — повторять нечего, доступа к каналу физически нет.
 */
export type LocationFallbackReason = 'unsupported' | 'unavailable' | 'denied' | 'error';

/**
 * Геолокация. Фолбэк обязателен: LocationManager требует Bot API 8.0+,
 * а пользователь может отказать в доступе. Без запасного пути часть
 * пользователей не сможет оформить заказ, и в логах это не будет видно.
 *
 * `onFallback` получает причину — раньше отказ был неотличим от ошибки, и
 * экран молча переключался на карту без единого слова объяснения.
 */
export function requestLocation(
  onSuccess: (p: { lat: number; lon: number }) => void,
  onFallback: (reason: LocationFallbackReason) => void,
): void {
  const lm = tg?.LocationManager;
  if (!lm) return onFallback('unsupported');

  lm.init(() => {
    if (!lm.isLocationAvailable) return onFallback('unavailable');
    lm.getLocation((loc) => {
      if (loc) {
        onSuccess({ lat: loc.latitude, lon: loc.longitude });
        return;
      }
      onFallback(lm.isAccessRequested && !lm.isAccessGranted ? 'denied' : 'error');
    });
  });
}

/** Открывает настройки доступа к геолокации — есть смысл только после `denied`. */
export function openLocationSettings(): void {
  tg?.LocationManager?.openSettings?.();
}

/**
 * Поддерживает ли клиент запрос контакта прямо из мини-аппа (Bot API 6.9+).
 * На старых клиентах кнопку не показываем — предлагать нерабочее хуже, чем
 * не предлагать ничего.
 */
export function canRequestContact(): boolean {
  return typeof tg?.requestContact === 'function';
}

/**
 * Запрашивает телефон нативным попапом Telegram.
 *
 * Колбэк отдаёт только факт согласия — сам номер в JS не приходит. Telegram
 * шлёт `message.contact` в чат бота, как и при обычной кнопке в клавиатуре:
 * бэкенд и проверка `contact.user_id === ctx.from.id` не меняются, меняется
 * только то, что запросить это можно не выходя из мини-аппа.
 */
export function requestContact(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!tg?.requestContact) {
      resolve(false);
      return;
    }
    tg.requestContact((shared) => resolve(shared));
  });
}

/** Открыть t.me-ссылку, не покидая Telegram. Мини-апп при этом не закрывается. */
export function openTelegramLink(url: string): void {
  tg?.openTelegramLink?.(url);
}
